import http from "@/services/http";
import { useMutation } from "@tanstack/vue-query";
import { unref, type MaybeRef } from "vue";

/** One entry for POST /api/live_track_action/bulk-create (mirrors single-create body). */
export type LiveTrackActionBulkCreateItem = {
  live_track_id: string | number;
  live_track_code: string;
  source: string;
  update_fields?: Record<string, unknown> | null;
  update_audio_file?: string | null;
  update_cover_art?: string | null;
  raw_ddex_track_id?: number | null;
  live_track_backup?: Record<string, unknown> | null;
  rollback_to_action_id?: number | null;
};

export type LiveTrackActionBulkCreateResponse = {
  created: Array<{
    id: number;
    live_track_code: string;
    squashed_from_indices: number[];
  }>;
  errors: Array<{
    indices: number[];
    live_track_code: string | null;
    message: string;
  }>;
};

export async function postLiveTrackActionBulkCreate(
  items: LiveTrackActionBulkCreateItem[]
): Promise<LiveTrackActionBulkCreateResponse> {
  const { data } = await http.post<LiveTrackActionBulkCreateResponse>(
    "/api/live_track_action/bulk-create",
    { items }
  );
  return data;
}

/** Re-queues a failed LiveTrackAction. The backend creates a new action mirroring
 *  the original's parameters and dispatches the job afresh. Returns 422 when
 *  the action is no longer retryable (see backend `can_retry`). */
export async function postLiveTrackActionRetry(actionId: number): Promise<void> {
  await http.post(`/api/live-track-actions/${actionId}/retry`);
}

export const useUpdateMutation = (entitySlug: MaybeRef<string>) => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) => {
      return http.put(`/api/${unref(entitySlug)}/${id}`, data);
    },
  });
};

export const useCreateMutation = (entitySlug: MaybeRef<string>) => {
  return useMutation({
    mutationFn: (data: unknown) => http.post(`/api/${unref(entitySlug)}`, data),
  });
};
